import { HttpHeaders } from '@angular/common/http';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, map, Observable, of, race, skipWhile, Subject, switchMap, take, takeUntil } from 'rxjs';
import { DataType } from 'src/app/pbir/dataModel/DataType';
import { ExecuteDataQueryElement, ResultSetDefinition, SortDirection } from 'src/app/services/api.services';
import { SchemaUtils } from 'src/app/shared/utils/schema-utils';
import { VisualDataRequest } from '../../VisualDataRequest';
import { ChunkResult, ColumnOrder, Rows, RowsResult, VisualDataQueryTable, VisualDataTableResourceOptions } from '../../VisualDataSource';
import { DataQueryVisualDataSource } from '../DataQueryVisualDataSource';
import { AdapterImpl, RESULTSET_MODEL_TABLE } from './AdapterImpl';
import { ColumnCollectionImpl } from './ColumnCollectionImpl';
import { ColumnImpl } from './ColumnImpl';
import { ResourceImpl } from './ResourceImpl';
import { ResourceManagerImpl } from './ResourceManagerImpl';
import { isNullOrUndefined } from 'src/app/shared/utils';

export class TableImpl implements VisualDataQueryTable {
  constructor(
    public readonly request: VisualDataRequest,
    public readonly dataSource: DataQueryVisualDataSource,
    public readonly resources: ResourceManagerImpl,
    public readonly queryId: string,
    public readonly queryElements: ReadonlyArray<ExecuteDataQueryElement>,
    public readonly resultSetIndex: number,
    public readonly resultSet: ResultSetDefinition) { }

  get injector() { return this.dataSource.injector; }
  get cache() { return this.dataSource.cache; }
  get dataQueryService() { return this.dataSource.dataQueryService; }
  get httpClient() { return this.dataSource.httpClient; }

  readonly adapter = new AdapterImpl(this);

  readonly columns =
    new ColumnCollectionImpl(...this.queryElements
      .filter(e => e.resultSetIndex == this.resultSetIndex)
      .map(e => ({ e, n: e.elementName.toLowerCase() }))
      .map(({ e, n }) => ({
        e,
        re: this.resultSet.elements.find(x => x.name.toLowerCase() == n)
      }))
      .filter(({ re }) => !!re)
      .map(({ e, re }) => ({
        e, re,
        f: this.request.fields.find(x => e.field?.id   == x.name 
                                      || e.elementName == x.name
                                      || SchemaUtils.sameTargets(x.target.schemaFieldTargetDto, e.target, false))
      }))
      .filter(({ f }) => !!f)
      .map(({ e, re, f }, i) => {
        return <ColumnImpl>{
          index: i,
          target: f.target,

          dataType: DataType.from(e.dataType),
          dataIndex: re.dataIndex,

          name: e.elementName,
          identifier: `[${RESULTSET_MODEL_TABLE}].[${re.dataColumn}]`,

          usage: e.usage,
          baseElement: e.baseElementName,

          element: e,
          resultSetElement: re,
        };
      })
    );

  readonly totalRows = this.resultSet.rowCount;
  readonly exceededRowLimits = this.resultSet.exceededRowCountLimit;

  readonly rowsPerChunk = 500;
  readonly chunks = Math.max(1, Math.ceil(this.totalRows / this.rowsPerChunk));


  chunk(chunkIndex: number, options?: VisualDataTableResourceOptions): ResourceImpl<ChunkResult> {
    let lastChunk = this.chunks - 1;

    if (chunkIndex < 0 || chunkIndex > lastChunk)
      throw new Error(`Invalid chunk index`, { cause: chunkIndex });

    let resourceOptions = { ...options };

    return this.resources.getOrCreate(
      this, this.key(['chunk', chunkIndex], resourceOptions),
      (aborted) => {
        let columns = this.columns;
        let totalRows = this.totalRows;
        let rowsPerChunk = this.rowsPerChunk;

        let skip = resourceOptions?.skip ?? 0;
        let take = resourceOptions?.take ?? rowsPerChunk;

        let rowOffset = Math.min((chunkIndex * rowsPerChunk) + skip, totalRows);
        let rowCount = Math.min(totalRows - rowOffset, take);

        let last = chunkIndex == lastChunk;

        if ((rowCount < 1) || (rowOffset >= totalRows)) {
          return of(<ChunkResult>{
            index: chunkIndex,
            columns,
            last,
            rowOffset,
            rows: []
          });
        }

        return this.createResultSetGetDataRequest(
          rowOffset,
          rowCount,
          resourceOptions?.order,
          aborted
        ).pipe(
          takeUntil(aborted),
          map(rows => {
            if (!Array.isArray(rows))
              throw new Error(`Invalid response`, { cause: rows });

            return <ChunkResult>{
              index: chunkIndex,
              columns,
              last,
              rowOffset,
              rows
            };
          }),
        );
      }, {
        abortSignal: resourceOptions.abortSignal,
        debugName: [
          this.request.debugName, 
          'chunk',
          chunkIndex,
          options?.debugName
        ].filter(x => !isNullOrUndefined(x)).join('.') 
      }
    );
  }

  rows(options?: VisualDataTableResourceOptions): ResourceImpl<RowsResult> {
    let resourceOptions = { ...options };

    return this.resources.getOrCreate(
      this, this.key(['rows'], resourceOptions), 
      (aborted) => {
        let columns = this.columns;
        let totalRows = this.totalRows;
        let rowsPerChunk = this.rowsPerChunk;

        let rowOffset = Math.min(totalRows, resourceOptions?.skip ?? 0);
        let rowCount  = Math.min(totalRows - rowOffset, resourceOptions?.take ?? totalRows);

        if ((rowCount < 1) || (rowOffset >= totalRows)) {
          return of(<RowsResult>{
            columns,
            rowOffset,
            rows: []
          });
        }

        let startChunkIndex = Math.floor(rowOffset / this.rowsPerChunk);
        let endChunkIndex   = Math.floor((rowOffset + rowCount - 1) / this.rowsPerChunk);

        let anyAborted = new Subject<void>();
        let chunkResources: Observable<ChunkResult>[] = [];

        let _aborted = race(anyAborted, aborted);

        for (let i = startChunkIndex; i <= endChunkIndex; ++i) {
          let chunkRowOffset = i * rowsPerChunk;

          let chunkSkip = Math.max(0, rowOffset - chunkRowOffset);
          let chunkTake = Math.min(rowCount, totalRows - (chunkRowOffset + chunkSkip), rowsPerChunk);
          
          let chunkOptions = {
            ...resourceOptions,
            skip: chunkSkip,
            take: chunkTake
          };

          let chunkResource = this.chunk(i, chunkOptions);

          let aborted$ = race(chunkResource.abortedSubject, _aborted);

          chunkResources.push(
            toObservable(chunkResource.completed, { injector: this.injector }).pipe(
              takeUntil(aborted$),
              skipWhile(c => !c),
              take(1),
              takeUntil(aborted$),
              map(() => {
                if (chunkResource.success()) {
                  let result = chunkResource.result();

                  if (chunkSkip > 0) {
                    result = {
                      ...result,
                      rowOffset: chunkRowOffset + chunkSkip,
                      rows: result.rows.slice(0, chunkTake)
                    };
                  }

                  return result;
                }
                else {
                  anyAborted.next();
                  return null;
                }
              }),
            )
          );
        }

        return combineLatest(chunkResources).pipe(
          map(x => (<RowsResult>{
            columns: columns,
            rowOffset,
            rows: x.map(c => c.rows).flat(1)
          })),
          takeUntil(_aborted)
        );
      }, {
        abortSignal: resourceOptions.abortSignal,
        debugName: [
          this.request.debugName,
          options?.debugName
        ].filter(x => !isNullOrUndefined(x)).join('.') }
    );
  }

  private key(prefix: any[], options?: VisualDataTableResourceOptions) {
    return [
      ...prefix,
      ...(options?.order?.map(o => [o.columnIndex ?? 'null', o.sort ?? 'null']) ?? []),
      options?.skip ?? 0,
      options?.take ?? '*'
    ].join('\n');
  }

  private createResultSetGetDataRequest(
    rowOffset: number,
    rowCount: number,
    order: ReadonlyArray<ColumnOrder>,
    aborted: Observable<void>): Observable<any> {
    return this.dataSource.dataQueryService.getResultSetDataUrl({
      queryId: this.queryId,
      resultSetId: this.resultSet.id
    }).pipe(
      takeUntil(aborted),
      switchMap(getDataUrl => {
        let queryParams = [
          `skip=${encodeURIComponent(rowOffset)}`,
          `take=${encodeURIComponent(rowCount)}`,
        ];

        let orderParams = order?.map(o => `${this.columns[o.columnIndex].dataIndex}=${(o.sort == SortDirection.Ascending ? 'asc' : 'desc')}`);
        if (orderParams?.length) {
          queryParams.push(`order=${encodeURIComponent(orderParams.join(','))}`);
        }

        let queryString = queryParams.join('&');

        let url = `${getDataUrl.url}?${queryString}`;

        return this.httpClient.request(getDataUrl.method, url, {
          headers: new HttpHeaders(getDataUrl.headers),
          responseType: 'json',

          transferCache: false,
          withCredentials: false,
        });
      })
    );
  }
}

export interface StreamedResultSetDataQueryResult {
  readonly resultSet: ResultSetDefinition;
  readonly rows: Rows;
}

